import { PathInfoState } from "./path-info.component";
import {
  getElevationGain,
  getMaxHeight,
  getMinHeight,
  getTotalElevation,
  getLineHeight,
  getTextHeight,
  getCumulativeDistances,
  difficultyMappings,
  formatLength,
  replaceWithBr,
} from "./utils";
import styles from "./path-info.module.css";
import {
  getPartialPathIcon,
  getPathColor,
  getPathDifficultyIcon,
} from "../../utils/rendering.utils";
import { MapCategories } from "../../interfaces/map/common.interfaces";
import { ReactComponent as CloseIcon } from "../../assets/images/close.svg";
import { useMapContext } from "../../hooks/use-map-context";
import { postMessage } from "../../utils/map.utils";
import {
  PathCategoryResponse,
  PathTagResponse,
} from "../../interfaces/backend/path.interface";

type Props = {
  isStandAlone: boolean;
  state: PathInfoState;
  mapPathCategories: MapCategories;
  onClose: () => void;
};

export const StandardPathInfo = ({
  isStandAlone,
  state,
  mapPathCategories,
  onClose,
}: Props) => {
  const { useEnglish, showGpxDownload: gpxAllowed } = useMapContext();
  const showGpxDownload = gpxAllowed && (mapPathCategories[`${state.pathCategoryId}`]?.showGpxDownload ?? false);
  const type = mapPathCategories[`${state.pathCategoryId}`]?.identifier;

  const Icon = (props: any) => getPartialPathIcon(type);
  const color = getPathColor(type);
  const DifficultyIcon = (props: any) =>
    getPathDifficultyIcon(state.difficulty, props);

  const renderIcon = () => (
    <>
      <Icon className={styles.icon} style={{ color: color }} />
      <span className={isStandAlone ? styles.iconText : styles.iconTextMobile}>
        {state.number}
      </span>
    </>
  );
  const elevationGain = getElevationGain(state.pathGpsCoordinates);

  const profileWidth = 7000;
  const profileHeight = 1800;
  const cumulativeDistances = getCumulativeDistances(state.pathGpsCoordinates);
  const totalPathDistance = cumulativeDistances[cumulativeDistances.length - 1] || 1;
  const testMaxHeight = getMaxHeight(state.pathGpsCoordinates);
  const testMinHeight = getMinHeight(state.pathGpsCoordinates);
  const testTotalElevation = getTotalElevation(state.pathGpsCoordinates);
  const lineHeight = getLineHeight(
    testMinHeight,
    testMaxHeight,
    testTotalElevation
  );

  const textMaxHeight = getTextHeight(testMaxHeight);
  const textMinHeight = getTextHeight(testMinHeight);

  const handleDownloadGpx = () => {
    if (!state.id || isStandAlone) return;
    postMessage(JSON.stringify({ gpxDownload: true, pathId: state.id }));
  };

  const difficultyName = difficultyMappings(state.difficulty, useEnglish);

  const tagNameKey: keyof PathTagResponse = useEnglish ? "name_en" : "name";
  const pathCategoryNameKey: keyof PathCategoryResponse = useEnglish
    ? "name_en"
    : "name";
  const metadata =
    state.tags && state.tags.length
      ? `${difficultyName}, ${state.tags
          .map((tag) => tag[tagNameKey])
          .join(", ")}`
      : difficultyName;

  const getElevationGainText = () => {
    const elevationGainValue = elevationGain.toFixed(0);
    if (useEnglish) return `Total elevation gain: ${elevationGainValue} m`;
    return `Total stigning: ${elevationGainValue} m`;
  };
  const elevationGainText = getElevationGainText();

  const description = useEnglish ? state.description_en : state.description;
  return (
    <div
      className={
        isStandAlone
          ? styles.pathInfoContainerStandAlone
          : styles.pathInfoContainer
      }
      id="sheet"
    >
      <div className={styles.header}>
        <div className={styles.handleWrapper} id="handle">
          <div className={styles.dragHandle} />
        </div>
        <div className={styles.headerInfoSection}>
          <div
            style={{ color: color }}
            className={
              isStandAlone ? styles.iconContainer : styles.iconContainerMobile
            }
          >
            {renderIcon()}
          </div>
          <div className={styles.headerRightCol}>
            <h1 className={isStandAlone ? styles.title : styles.titleMobile}>
              {state.name}
            </h1>
            <div className={styles.difficultyRow}>
              <DifficultyIcon
                className={
                  isStandAlone
                    ? styles.difficultyIcon
                    : styles.difficultyIconMobile
                }
              />
              <div className={styles.difficultyText}>{metadata}</div>
            </div>
            <div className={styles.statusRow}>
              <div className={styles.length}>{formatLength(state.length)}</div>
              <div className={styles.statusText}>
                {state.pathCategory
                  ? state.pathCategory[pathCategoryNameKey]
                  : ""}
              </div>
            </div>
          </div>
          <CloseIcon className={styles.closeButton} onClick={onClose} />
        </div>
      </div>

      <div
        style={{
          flex: 1,
          height: isStandAlone ? 200 : 110,
          marginBottom: !isStandAlone ? 0 : 5,
          marginLeft: !isStandAlone ? 16 : 0,
          marginRight: !isStandAlone ? 16 : 0,
        }}
      >
        <svg
          height={isStandAlone ? "200px" : "110px"}
          width={isStandAlone ? "100%" : "100%"}
          preserveAspectRatio="none"
          viewBox={`0 0 ${profileWidth} ${profileHeight}`}
          style={{ backgroundColor: "#D1E3EC" }}
        >
          <polyline
            fill="#82A996"
            stroke="#82A996"
            strokeWidth="3"
            points={`${state.pathGpsCoordinates?.map(
              (coord, index) =>
                `${(cumulativeDistances[index] / totalPathDistance) * profileWidth},${
                  profileHeight -
                  350 -
                  (coord.elevation - testMinHeight) *
                    ((100 / (testTotalElevation + 100)) * 14)
                }`
            )},
        ${profileWidth}, ${profileHeight}, 0, ${profileHeight}`}
          />

          <line
            x1={0}
            y1={profileHeight - 350 - lineHeight}
            x2={profileWidth}
            y2={profileHeight - 350 - lineHeight}
            fill="rgba(153, 153, 153, .5)"
            stroke="rgba(153, 153, 153, .5)"
            strokeWidth="30"
            strokeLinecap="round"
            strokeDasharray="1, 80"
          />

          <text
            fill="#999999"
            stroke="#999999"
            strokeWidth="8"
            fontSize="215"
            fontStretch="normal"
            x={profileWidth - 40}
            y={profileHeight - 400 - lineHeight}
            textAnchor="end"
          >
            {textMaxHeight}
          </text>

          <line
            x1={0}
            y1={profileHeight - 350}
            x2={profileWidth}
            y2={profileHeight - 350}
            fill="rgba(255, 255, 255, .5)"
            stroke="rgba(255, 255, 255, .5)"
            strokeWidth="30"
            strokeLinecap="round"
            strokeDasharray="1, 80"
          />

          <text
            fill="#FFFFFF"
            stroke="#FFFFFF"
            strokeWidth="8"
            fontSize="215"
            fontStretch="normal"
            x={profileWidth - 40}
            y={profileHeight - 100}
            textAnchor="end"
          >
            {textMinHeight}
          </text>
        </svg>
      </div>
      <div className={styles.descriptionContainer}>
        <div className={styles.elevationText}>{elevationGainText}</div>
        {showGpxDownload && (
          <button className={styles.gpxDownloadButton} onClick={handleDownloadGpx}>
            Ladda ner gpx
          </button>
        )}
        <div
          className={styles.descriptionText}
          dangerouslySetInnerHTML={{
            __html: replaceWithBr(description),
          }}
        />
      </div>
    </div>
  );
};
